import { useState } from 'react';

import {
  getNotificationListQueryKeyPrefix,
  useNotificationsQuery,
} from '@frontend/api';
import { queryClient } from '@frontend/api';
import { DATE_RANGE_PRESETS } from '@frontend/api/src/notifications/constants';
import {
  Box,
  Button,
  Flex,
  Tab,
  Tabs,
  TabQuantityBadge,
  TabList,
  TabPanel,
  TabPanels,
  Empty,
  EmptyDescription,
  EmptyHeadline,
  EmptyDrawerIcon,
} from '@frontend/legacy-components';

import type { FiltersType } from 'modules/notifications/types';

import AppliedFiltersContainer from './components/AppliedFiltersContainer';
import MarkAllNotificationsReadButton from './components/MarkAllNotificationsReadButton';
import NotificationFilterControls from './components/NotificationFilterControls';
import NotificationsTabPanel from './components/NotificationsTabPanel';
import { useNotificationFilters } from './data/hooks';

const getFilterStatus = (filters: FiltersType) => {
  const copy = { ...filters };

  delete copy.page;
  delete copy.searchTerm;
  delete copy.filterOutAcknowledged;
  /**
   * We do not want to consider the "page" or "searchTerm" or "filterOutAcknoweledged properties in determining whether or not we have applied filters as they are not available filters to apply in the popover.
   */
  for (const filter in copy) {
    if (copy[filter] && copy[filter] !== DATE_RANGE_PRESETS.NONE) return true;
  }
  return false;
};

const NotificationsPage = () => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [notificationFilters, setNotificationFilters] =
    useNotificationFilters();
  const { data, isError } = useNotificationsQuery(notificationFilters);

  const totalItems = data?.count;
  const unacknowledgedCount = data?.unacknowledgedCount;
  const currentPage = notificationFilters?.page || 1;
  const pageSize = 10;
  const unread = notificationFilters?.filterOutAcknowledged;
  const areFiltersApplied = getFilterStatus(notificationFilters);

  // Remove all query parameters if the query returns an error (perhaps because the page number is too high or something)
  if (isError) {
    setNotificationFilters({});
  }

  if (!data) {
    return null;
  }

  return (
    <Flex padding={{ base: 2, tablet: 8 }}>
      <Flex
        flexDir="column"
        marginLeft="auto"
        marginRight="auto"
        maxWidth="1344px"
        flex="1 0 auto"
        width="100%"
      >
        <NotificationFilterControls
          setFilters={setNotificationFilters}
          filters={notificationFilters}
          isFiltersPopoverOpen={isFiltersOpen}
          setIsFiltersPopoverOpen={setIsFiltersOpen}
        />
        {areFiltersApplied && (
          <AppliedFiltersContainer
            filters={notificationFilters}
            setFilters={setNotificationFilters}
          />
        )}
        <Tabs
          variant="enclosed"
          index={unread ? 1 : 0}
          overflow="clip"
          onChange={(index: number) => {
            switch (index) {
              case 0:
                setNotificationFilters({
                  ...notificationFilters,
                  page: 1,
                  filterOutAcknowledged: undefined,
                });
                break;
              case 1:
                setNotificationFilters({
                  ...notificationFilters,
                  page: 1,
                  filterOutAcknowledged: true,
                });
                break;
              default:
                break;
            }
          }}
        >
          <TabList position="relative">
            <Tab>All</Tab>
            <Tab>
              Unread<TabQuantityBadge>{unacknowledgedCount}</TabQuantityBadge>
            </Tab>
            <Box position="absolute" right={1} top={1.5} padding={0}>
              <MarkAllNotificationsReadButton
                callback={() => {
                  queryClient.invalidateQueries({
                    queryKey: getNotificationListQueryKeyPrefix(),
                  });
                }}
              />
            </Box>
          </TabList>
          <TabPanels
            borderRight="1px solid"
            borderRightColor="separator.border"
            borderBottom="1px solid"
            borderBottomColor="separator.border"
            borderLeft="1px solid"
            borderLeftColor="separator.border"
            borderBottomRadius="lg"
          >
            <TabPanel padding={0}>
              <NotificationsTabPanel
                notifications={data?.results || []}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={pageSize}
                onChangePage={(page) => {
                  setNotificationFilters({
                    ...notificationFilters,
                    page,
                  });
                }}
              />
            </TabPanel>
            <TabPanel padding={0}>
              <NotificationsTabPanel
                notifications={data?.results || []}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={pageSize}
                onChangePage={(page) => {
                  setNotificationFilters({
                    ...notificationFilters,
                    page,
                  });
                }}
                customEmptyComponent={
                  <Empty padding={1} size="sm">
                    <EmptyDrawerIcon />
                    <EmptyHeadline>No notifications yet</EmptyHeadline>
                    <EmptyDescription>
                      To view read messages, go to &quot;
                      {
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() =>
                            setNotificationFilters({
                              ...notificationFilters,
                              filterOutAcknowledged: undefined,
                            })
                          }
                        >
                          All
                        </Button>
                      }
                      .&quot;
                    </EmptyDescription>
                  </Empty>
                }
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Flex>
  );
};

export default NotificationsPage;
